import React from 'react';
import { observer } from 'mobx-react';
import { Flex } from 'components/Layout/Layout';
import styled, { css } from 'styled-components';
import Carousel from 'nuka-carousel';
import { FormattedMessage, useIntl } from 'react-intl';
import { ButtonStyle } from 'components/Button/Button';

import messages from '../messages';
import { LabelStyle } from '../../../components/Label/Label';

const ShareButton = styled.button`
    ${ButtonStyle};
  
    position: absolute;
    right: 5px;
    top: 5px;
    z-index: 100;
    padding: 8px 16px;
    text-transform: uppercase;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
}`;

const SharedLabel = styled.span`
  ${LabelStyle};
  font-size: 14px;
  user-select: none;
  cursor: help;
  position: absolute;
  top: 10px;
  right: 10px;
`;

const ThumbWrapper = styled.div`
  overflow: hidden;
  cursor: pointer;
  /* border: 1px solid red; */
  background-color: ${props => props.theme.colors.darkgray};
  background-image: url(${props => props.img});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  width: auto;
  height: 270px;

  &:after {
    box-sizing: border-box;
    transition: all 0.2s ease-in-out;
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    border: 2px solid ${props => props.theme.colors.xDarkgray};
  }

  ${props =>
    props.isactive &&
    css`
      &:after {
        border: 5px solid ${props => props.theme.colors.primary};
      }
    `} 

  ${props =>
    props.isactive &&
    props.sharedToMe &&
    css`
      &:after {
        border: 2px solid ${props => props.theme.colors.secondary};
      }
    `} 
    
    ${props =>
      props.$loading &&
      css`
        &:after {
          border: 0;
        }
      `} 
      
    &:hover {
      &:after {
        border: 2px solid ${props => props.theme.colors.white};
      }


      ${props =>
        props.isactive &&
        css`
          &:after {
            border: 5px solid ${props => props.theme.colors.primary10};
          }
        `}
      
      ${props =>
        props.isactive &&
        props.sharedFrom &&
        css`
          &:after {
            border: 5px solid ${props => props.theme.colors.secondary};
          }
        `}

      ${props =>
        props.$loading &&
        css`
          &:after {
            border: 0;
          }
        `};
    }
`;

const BouncingLoader = styled.div`
  @keyframes bouncing-loader {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0.1;
      transform: translateY(-1rem);
    }
  }

  display: flex;
  justify-content: center;

  & > div {
    width: 1rem;
    height: 1rem;
    margin: 3rem 0.2rem;
    background: #8385aa;
    border-radius: 50%;
    animation: bouncing-loader 0.6s infinite alternate;
  }
  & > div:nth-child(2) {
    animation-delay: 0.2s;
  }
  & > div:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

export const Thumb = observer(
  ({
    video,
    onClick,
    onShare,
    isactive,
    canPlayLocal,
    sharedBy,
    sharedToMe,
    shareable,
    videoAnalyse
  }) => {
    let isLoading = !video.transcoded || !video.thumb;
    if (isLoading && canPlayLocal) {
      isLoading = false;
      console.log('video.thumb', video.thumb);
    }
    return (
      <React.Fragment>
        <ThumbWrapper
          $loading={isLoading}
          isactive={isactive}
          img={canPlayLocal ? '/assets/images/fallback.png' : video.thumb}
          role="button"
          onClick={() => onClick()}
          sharedToMe={sharedToMe}
        >
          {isLoading && (
            <Flex
              alignItems="center"
              justifyContent="center"
              style={{ height: '100%' }}
            >
              <BouncingLoader class="bouncing-loader">
                <div />
                <div />
                <div />
              </BouncingLoader>
            </Flex>
          )}
        </ThumbWrapper>
        {shareable ? (
          <ShareButton
            small
            primaryInverted
            onClick={() => onShare && onShare(videoAnalyse)}
          >
            <FormattedMessage {...messages.analyseShareButton} />
          </ShareButton>
        ) : (
          ''
        )}
        {sharedToMe ? (
          <SharedLabel
            tiny
            secondary
            title={`${sharedBy.firstname} ${sharedBy.lastname}`}
          >
            <FormattedMessage {...messages.analyseSharedLabel} />
          </SharedLabel>
        ) : (
          ''
        )}
      </React.Fragment>
    );
  }
);

const AnalyseCaroussel = observer(
  ({ items = [], activeId, user, onClick, onShare }) => {
    const intl = useIntl();
    return (
      <Carousel
        slidesToShow={3}
        cellAlign="left"
        cellSpacing={15}
        initialSlideHeight={265}
        renderCenterLeftControls={({ previousSlide }) =>
          items.length > 3 ? (
            <button className="slider-button" onClick={previousSlide}>
              <FormattedMessage {...messages.analiseOverviewSliderPrevious} />
            </button>
          ) : (
            false
          )
        }
        renderBottomCenterControls={({ currentSlide }) => false}
        renderCenterRightControls={({ nextSlide }) =>
          items.length > 3 ? (
            <button className="slider-button" onClick={nextSlide}>
              <FormattedMessage {...messages.analiseOverviewSliderNext} />
            </button>
          ) : (
            false
          )
        }
      >
        {items.map(
          (data, index) =>
            data &&
            data.video && (
              <Thumb
                isactive={activeId ? data.id === activeId : false}
                key={data.id}
                video={data.video}
                onClick={() => onClick(data)}
                onShare={() => onShare(data)}
                sharedBy={data.sharedFrom}
                sharedToMe={data.sharedFrom && data.sharedFrom.id !== user.id}
                shareable={data.sharedFrom && data.sharedFrom.id === user.id}
                videoAnalyse={data}
                canPlayLocal={false}
              />
            )
        )}
      </Carousel>
    );
  }
);

export default AnalyseCaroussel;
